import type { SupRecordUnsupportedReason } from "@/components/Ui/Tooltip/Unsupported";
import type { SupSingleVaultV3 } from "@/types";

import useGetAdminConfig from "@/hooks/api/useGetAdminConfig";
import { useStoredData } from "@/store";

import { useMemo } from "react";

/**
 * Logic behind the deposit button's state and tooltip
 *
 * @param {SupSingleVaultV3} singleVault
 * @returns {{reason: SupRecordUnsupportedReason; isDisabled: boolean; isCompleted: boolean}}
 */
export default function useDepositButton(singleVault: SupSingleVaultV3): { reason: SupRecordUnsupportedReason; isDisabled: boolean; isCompleted: boolean; isLoading: boolean } {
  const storedData = useStoredData();

  const { data: adminConfig, isLoading } = useGetAdminConfig();

  const { isDepositDisabled, cartLimit, areAllDisabled, targetChainDisabled } = useMemo(() => {
    const restrictedTargetChains = adminConfig?.rawConfig.exclude_to_chains_deposits ?? [];
    return {
      areAllDisabled: (adminConfig?.isWithdrawalDisabled && adminConfig?.isDepositDisabled) ?? false,
      isDepositDisabled: adminConfig?.isDepositDisabled ?? false,
      cartLimit: adminConfig?.depositCartLimit ?? 1,
      targetChainDisabled: restrictedTargetChains.includes(singleVault.chain.id),
    };
  }, [adminConfig?.depositCartLimit, adminConfig?.isDepositDisabled, adminConfig?.isWithdrawalDisabled, adminConfig?.rawConfig.exclude_to_chains_deposits, singleVault.chain.id]);

  return useMemo(() => {
    const isUnavailable = ["unavailable", "paused", "withdraw_only"].includes(singleVault.availability);
    const isUnavailableReason = isUnavailable ? (["unavailable", "paused"].includes(singleVault.availability) ? "unavailable" : "depositUnavailable") : undefined;
    const isCartLimit = storedData.cart.length >= cartLimit && !storedData.cart.includes(singleVault.id);
    const isInCart = storedData.cart.includes(singleVault.slug);
    const vaultHasNoSuperform = parseInt(`${singleVault.form_implementation_id ?? 1}`) === 1 ? !singleVault.superform_address : !singleVault.underlying_wrapper_vaults;

    return {
      isLoading,
      reason: (areAllDisabled
        ? "disabled"
        : isDepositDisabled
        ? "disabledDeposit"
        : targetChainDisabled
        ? "disabledChain"
        : isUnavailable
        ? isUnavailableReason
        : vaultHasNoSuperform
        ? "unlisted"
        : isCartLimit
        ? "limit"
        : undefined) as SupRecordUnsupportedReason,
      isDisabled: isDepositDisabled || isUnavailable || vaultHasNoSuperform || (isCartLimit && !isInCart) || targetChainDisabled,
      isCompleted: isInCart,
    };
  }, [
    singleVault.availability,
    singleVault.id,
    singleVault.slug,
    singleVault.form_implementation_id,
    singleVault.superform_address,
    singleVault.underlying_wrapper_vaults,
    storedData.cart,
    cartLimit,
    isLoading,
    areAllDisabled,
    isDepositDisabled,
    targetChainDisabled,
  ]);
}
